import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Popper,
  Collapse,
  Chip,
  FormHelperText,
  InputAdornment,
  ClickAwayListener,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Cancel, Close } from "@mui/icons-material";

import {
  StyledCheckbox,
  StyledMenuItemContainer,
  StyledMenuListItemText,
  StyledSubMenuItemContainer,
} from "../StyledComponents/styled";

import { CustomTextInput } from "../TextInputs/style";

const BOTTOM_MARGIN_PX = 100;
const TOP_MARGIN_PX = 8;
const MIN_DROPDOWN_HEIGHT = 150;
const FLIP_THRESHOLD_TO_TOP = 200;
const FLIP_THRESHOLD_TO_BOTTOM = 250;

const DROPDOWN_OPENED_EVENT = "dropdown-singleton-opened";

/**
 * DynamicHierarchicalMultiselect
 *
 * Data shape (default keys):
 * [
 *   {
 *     category_name: "1-test",
 *     options: [{ value: "123", label: "prod1" }, ...]
 *   }
 * ]
 *
 * Props allow remapping keys so you can reuse with different APIs.
 */
const CustomizedHierarchicalMultiselect = ({
  label,
  categories,
  value,
  onChange,
  fieldErrors = "",
  categoryLabelKey = "category_name",
  optionsKey = "options",
  optionValueKey = "value",
  optionLabelKey = "label",
  keepOpenOnScroll,
}) => {
  const anchorRef = useRef(null);
  const itemRefs = useRef({});
  const paperRef = useRef(null);
  const searchInputRef = useRef(null);
  const optionRefs = useRef({});
  const popperRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState(MIN_DROPDOWN_HEIGHT);
  const [anchorIsFixed, setAnchorIsFixed] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Track placement both as state (for render) and as ref (for stable reads in callbacks)
  const placementRef = useRef("bottom-start");
  const [actualPlacement, setActualPlacement] = useState("bottom-start");

  const selectedValues = Array.isArray(value) ? value : [];
  const shrink = isFocused || open || selectedValues.length > 0;

  const instanceId = useRef(
    `hierarchical-multi-select-${Math.random().toString(36).substr(2, 9)}`
  );

  const updatePlacement = useCallback((p) => {
    placementRef.current = p;
    setActualPlacement(p);
  }, []);

  // Determine placement using hysteresis
  const computePlacement = useCallback(() => {
    if (!anchorRef.current) return placementRef.current;

    const { bottom } = anchorRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - bottom;
    const current = placementRef.current;
    const isTop = current.startsWith("top");

    if (!isTop && spaceBelow < FLIP_THRESHOLD_TO_TOP) return "top-start";
    if (isTop && spaceBelow > FLIP_THRESHOLD_TO_BOTTOM) return "bottom-start";
    return current;
  }, []);

  // Compute max dropdown height based on resolved placement
  const computeHeight = useCallback((placement) => {
    if (!anchorRef.current) return MIN_DROPDOWN_HEIGHT;

    const { top, bottom } = anchorRef.current.getBoundingClientRect();
    const openingUpward = placement.startsWith("top");
    const available = openingUpward
      ? top - TOP_MARGIN_PX
      : window.innerHeight - bottom - BOTTOM_MARGIN_PX;

    return Math.max(available, MIN_DROPDOWN_HEIGHT);
  }, []);

  // Resolve placement first, then derive height from that placement
  const recalculate = useCallback(() => {
    const placement = computePlacement();
    updatePlacement(placement);
    setDropdownMaxHeight(computeHeight(placement));
  }, [computePlacement, computeHeight, updatePlacement]);

  const detectAnchorIsFixed = useCallback(() => {
    let el = anchorRef.current?.parentElement;

    // Pre-scan the full ancestor chain for a Dialog before evaluating overflow;
    // DialogContent (overflow: auto) sits below the Dialog root in the DOM, so a
    // simple top-down traversal would return early at the overflow node and never
    // reach role="dialog"
    let insideDialog = false;
    let scanEl = el;
    while (scanEl && scanEl !== document.body) {
      if (scanEl.getAttribute("role") === "dialog") { insideDialog = true; break; }
      scanEl = scanEl.parentElement;
    }

    while (el && el !== document.body) {
      const style = window.getComputedStyle(el);
      const overflow = style.overflow + style.overflowY + style.overflowX;

      if (/auto|scroll|overlay/.test(overflow)) {
        // Anchor scrolls inside this container; fixed strategy is required when
        // that container belongs to a Dialog so the Popper can track the anchor
        return insideDialog;
      }

      if (style.position === "sticky" || style.position === "fixed") return true;

      el = el.parentElement;
    }

    return insideDialog;
  }, []);

  const close = useCallback((shouldRefocus = false) => {
    setOpen(false);
    setSearchTerm("");
    setHighlightedIndex(-1);
    if (shouldRefocus) anchorRef.current?.focus();
  }, []);

  // Singleton pattern: close this dropdown when another opens
  useEffect(() => {
    const handleOtherDropdownOpened = (e) => {
      if (e.detail?.id !== instanceId.current && open) close();
    };

    window.addEventListener(DROPDOWN_OPENED_EVENT, handleOtherDropdownOpened);
    return () => {
      window.removeEventListener(DROPDOWN_OPENED_EVENT, handleOtherDropdownOpened);
    };
  }, [open, close]);

  // Notify other dropdowns when this one opens
  useEffect(() => {
    if (open) {
      const event = new CustomEvent(DROPDOWN_OPENED_EVENT, {
        detail: { id: instanceId.current },
      });
      window.dispatchEvent(event);
    }
  }, [open]);

  // On open: resolve placement first, then compute height, then focus search
  useEffect(() => {
    if (!open) return;

    setAnchorIsFixed(detectAnchorIsFixed());
    recalculate();

    setTimeout(() => searchInputRef.current?.focus(), 0);
  }, [open, detectAnchorIsFixed, recalculate]);

  // On viewport resize or zoom: recalculate placement and height while open
  useEffect(() => {
    if (!open) return;

    const handleResize = () => recalculate();
    const vp = window.visualViewport;

    if (vp) {
      vp.addEventListener("resize", handleResize);
      return () => vp.removeEventListener("resize", handleResize);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [open, recalculate]);

  // Close when anchor exits the viewport in the direction the dropdown is open;
  // for fixed-strategy anchors also force a synchronous position update on scroll
  useEffect(() => {
    if (!open) return;

    const handleScroll = () => {
      if (anchorRef.current) {
        const { top, bottom } = anchorRef.current.getBoundingClientRect();
        const openingUpward = placementRef.current.startsWith("top");
        if (openingUpward && top > window.innerHeight) { close(false); return; }
        if (!openingUpward && bottom < 0) { close(false); return; }
      }
      // Absolute-strategy poppers follow document scroll natively; only fixed ones need JS nudging
      if (anchorIsFixed) popperRef.current?.forceUpdate();
    };

    window.addEventListener("scroll", handleScroll, { capture: true, passive: true });
    return () => window.removeEventListener("scroll", handleScroll, { capture: true });
  }, [open, anchorIsFixed, close]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchTerm]);

  const isSelected = useCallback(
    (v) => selectedValues.includes(v),
    [selectedValues]
  );

  const handleSelect = (v) => {
    const next = isSelected(v)
      ? selectedValues.filter((x) => x !== v)
      : [...selectedValues, v];
    onChange(next);
  };

  const handleRemove = (v) => {
    onChange(selectedValues.filter((x) => x !== v));
  };

  const handleCategoryToggle = (catKey) => {
    const wasExpanded = expanded[catKey];
    setExpanded((prev) => ({ ...prev, [catKey]: !prev[catKey] }));
    setHighlightedIndex(-1);

    if (!wasExpanded) {
      setTimeout(() => {
        const item = itemRefs.current[catKey];
        const container = paperRef.current;

        if (!item || !container) return;

        const itemRect = item.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        if (itemRect.bottom >= containerRect.bottom) {
          const scrollOffset = itemRect.top - containerRect.top - 8;
          container.scrollTo({
            top: container.scrollTop + scrollOffset,
            behavior: "smooth",
          });
        }
      }, 300);
    }
  };

  const handleCategorySelect = (optionValues, allSelected, e) => {
    e.stopPropagation();

    if (optionValues.length === 0) return;

    let next;
    if (allSelected) {
      next = selectedValues.filter((v) => !optionValues.includes(v));
    } else {
      const toAdd = optionValues.filter((v) => !isSelected(v));
      next = [...selectedValues, ...toAdd];
    }
    onChange(next);
  };

  const sortedCategories = useMemo(() => {
    const arr = Array.isArray(categories) ? [...categories] : [];
    return arr.sort((a, b) => {
      const aLabel = String(a?.[categoryLabelKey] ?? "");
      const bLabel = String(b?.[categoryLabelKey] ?? "");

      const aNum = parseInt(aLabel.split("-")[0], 10);
      const bNum = parseInt(bLabel.split("-")[0], 10);

      const aHasNum = !Number.isNaN(aNum);
      const bHasNum = !Number.isNaN(bNum);

      if (aHasNum && bHasNum) return aNum - bNum;
      return aLabel.localeCompare(bLabel, undefined, { numeric: true });
    });
  }, [categories, categoryLabelKey]);

  const searchValue = searchTerm.trim().toLowerCase();

  const isSearchActive = searchTerm.trim().length > 0;

  const optionsToList = useMemo(() => {
    return sortedCategories
      .map((cat) => {
        const allOptions = Array.isArray(cat?.[optionsKey]) ? [...cat[optionsKey]] : [];

        if (!searchValue) return cat;

        const lowerCaseCatName = String(cat?.[categoryLabelKey] ?? "").toLowerCase();
        if (lowerCaseCatName.includes(searchValue)) return cat;

        const filteredOptions = allOptions.filter((opt) => {
          const optVal = String(opt?.[optionValueKey] ?? "").toLowerCase();
          const optLabel = String(opt?.[optionLabelKey] ?? "").toLowerCase();
          return optVal.includes(searchValue) || optLabel.includes(searchValue);
        });

        if (filteredOptions.length > 0) return { ...cat, [optionsKey]: filteredOptions };

        return null;
      })
      .filter((cat) => cat !== null);
  }, [sortedCategories, searchValue, categoryLabelKey, optionsKey, optionValueKey, optionLabelKey]);

  // All selectable values across filtered categories, for the "Select All Results" row
  const filteredFlatValues = useMemo(() => {
    return optionsToList.flatMap((cat) =>
      (Array.isArray(cat?.[optionsKey]) ? cat[optionsKey] : [])
        .map((o) => o?.[optionValueKey])
        .filter((v) => v !== undefined && v !== null)
    );
  }, [optionsToList, optionsKey, optionValueKey]);

  const allFilteredSelected = useMemo(() => {
    if (filteredFlatValues.length === 0) return false;
    return filteredFlatValues.every((v) => isSelected(v));
  }, [filteredFlatValues, isSelected]);

  const someFilteredSelected = useMemo(() => {
    const count = filteredFlatValues.filter((v) => isSelected(v)).length;
    return count > 0 && !allFilteredSelected;
  }, [filteredFlatValues, isSelected, allFilteredSelected]);

  const handleSelectAllFiltered = useCallback(() => {
    if (allFilteredSelected) {
      const next = selectedValues.filter((v) => !filteredFlatValues.includes(v));
      onChange(next);
    } else {
      const toAdd = filteredFlatValues.filter((v) => !isSelected(v));
      onChange([...selectedValues, ...toAdd]);
    }
  }, [allFilteredSelected, filteredFlatValues, isSelected, selectedValues, onChange]);

  // Flat list of all keyboard-navigable items for arrow key handling
  const flatItemsList = useMemo(() => {
    const list = [];

    // Index 0 is reserved for the "Select All Results" row when search is active
    if (isSearchActive) {
      list.push({ type: "select-all-results" });
    }

    optionsToList.forEach((cat) => {
      const catKey = String(cat?.[categoryLabelKey] ?? "");
      list.push({ type: "group", catKey });

      if (expanded[catKey]) {
        const catOptions = Array.isArray(cat?.[optionsKey]) ? cat[optionsKey] : [];
        const sorted = [...catOptions].sort((a, b) => {
          const av = String(a?.[optionValueKey] ?? "");
          const bv = String(b?.[optionValueKey] ?? "");
          return av.localeCompare(bv, undefined, { numeric: true });
        });
        sorted.forEach((opt) => {
          const optValue = opt?.[optionValueKey];
          if (optValue !== undefined && optValue !== null) {
            list.push({ type: "option", optValue, catKey });
          }
        });
      }
    });

    return list;
  }, [optionsToList, expanded, isSearchActive, categoryLabelKey, optionsKey, optionValueKey]);

  const scrollHighlightedIntoView = useCallback((index) => {
    const element = optionRefs.current[`item-${index}`];
    if (element) element.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, []);

  const handleListKeyDown = useCallback(
    (e) => {
      if (!open) return;

      const totalCount = flatItemsList.length;
      if (totalCount === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          e.stopPropagation();
          setHighlightedIndex((prev) => {
            const next = prev < totalCount - 1 ? prev + 1 : 0;
            setTimeout(() => scrollHighlightedIntoView(next), 0);
            return next;
          });
          break;

        case "ArrowUp":
          e.preventDefault();
          e.stopPropagation();
          setHighlightedIndex((prev) => {
            const next = prev > 0 ? prev - 1 : totalCount - 1;
            setTimeout(() => scrollHighlightedIntoView(next), 0);
            return next;
          });
          break;

        case "Enter":
          e.preventDefault();
          e.stopPropagation();

          if (highlightedIndex < 0 || highlightedIndex >= flatItemsList.length) return;

          const item = flatItemsList[highlightedIndex];
          if (item.type === "select-all-results") {
            handleSelectAllFiltered();
          } else if (item.type === "group") {
            handleCategoryToggle(item.catKey);
          } else if (item.type === "option") {
            handleSelect(item.optValue);
          }
          break;

        case " ":
          if (e.target.tagName === "INPUT") return;
          e.preventDefault();
          e.stopPropagation();

          if (highlightedIndex < 0 || highlightedIndex >= flatItemsList.length) return;

          const spaceItem = flatItemsList[highlightedIndex];
          if (spaceItem.type === "select-all-results") {
            handleSelectAllFiltered();
          } else if (spaceItem.type === "option") {
            handleSelect(spaceItem.optValue);
          }
          break;

        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          close();
          break;

        default:
          break;
      }
    },
    [open, flatItemsList, highlightedIndex, close, scrollHighlightedIntoView, handleSelectAllFiltered]
  );

  // flatIndex is mutated during render to map visual positions to keyboard navigation indices;
  // when search is active, index 0 is already consumed by the "Select All Results" row
  let flatIndex = isSearchActive ? 0 : -1;

  return (
    <ClickAwayListener
      onClickAway={() => {
        if (open) close();
      }}
    >
      <Box sx={{ width: "100%", position: "relative" }}>
        <Box
          ref={anchorRef}
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          tabIndex={0}
          onClick={() => setOpen((prev) => !prev)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setOpen(true);
            }
            if (e.key === "Escape") close();
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault();
              if (!open) setOpen(true);
            }
          }}
          sx={{
            position: "relative",
            cursor: "pointer",
            outline: "none",
            pt: 2.1,
            borderBottom: "1px solid #AF3241",
            "&::after": {
              content: '""',
              position: "absolute",
              left: 0,
              bottom: -1,
              width: open ? "100%" : "0%",
              height: "2px",
              backgroundColor: "#AF3241",
              transition: "none",
            },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: shrink ? 0 : 22,
              fontSize: shrink ? 12 : 14,
              color: "#AF3241",
              pointerEvents: "none",
              transformOrigin: "top left",
              transition: "all 160ms ease",
            }}
          >
            {label}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 32 }}>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexWrap: "wrap",
                overflowX: "auto",
                overflowY: "hidden",
                gap: 0.5,
                py: 0.5,
                whiteSpace: "nowrap",
                "&::-webkit-scrollbar": { height: 6 },
              }}
              onClick={(e) => {
                e.stopPropagation();
                setOpen(true);
              }}
            >
              {selectedValues.length > 0 &&
                selectedValues.map((v) => (
                  <Chip
                    key={String(v)}
                    size="small"
                    label={String(v)}
                    onClick={(e) => e.stopPropagation()}
                    onDelete={(e) => {
                      e.stopPropagation();
                      handleRemove(v);
                    }}
                    deleteIcon={<Cancel sx={{ color: "#AF3241" }} />}
                    sx={{
                      flex: "0 0 auto",
                      backgroundColor: "#AF3241",
                      color: "#fff",
                      fontSize: "12px",
                      height: 24,
                      "& .MuiChip-deleteIcon": { color: "#fff" },
                    }}
                  />
                ))}
            </Box>

            <Box sx={{ flex: "0 0 auto", display: "flex", alignItems: "center" }}>
              {open ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </Box>
          </Box>
        </Box>

        {fieldErrors ? (
          <FormHelperText sx={{ color: "#AF3241", mt: 0.5 }}>{fieldErrors}</FormHelperText>
        ) : null}

        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement={actualPlacement}
          popperRef={popperRef}
          popperOptions={{ strategy: anchorIsFixed ? "fixed" : "absolute" }}
          sx={{ zIndex: 1400 }}
          modifiers={[
            {
              name: "eventListeners",
              options: { scroll: false, resize: false },
            },
            {
              // Placement and height are managed by recalculate(); the dropdown
              // scrolls naturally with its anchor rather than being pinned to the viewport
              name: "preventOverflow",
              enabled: false,
            },
            {
              // Flip is managed by recalculate(); disable Popper's built-in modifier
              name: "flip",
              enabled: false,
            },
          ]}
          style={{
            width: anchorRef.current ? anchorRef.current.offsetWidth : undefined,
          }}
        >
          <Paper
            ref={paperRef}
            role="listbox"
            tabIndex={-1}
            sx={{
              maxHeight: dropdownMaxHeight,
              overflowY: "auto",
              overflowX: "hidden",
              outline: "none",
            }}
            onMouseDown={(e) => {
              if (e.target.tagName !== "INPUT") e.preventDefault();
              e.stopPropagation();
            }}
            onKeyDown={handleListKeyDown}
          >
            <Box sx={{ p: 1 }} onClick={(e) => e.stopPropagation()}>
              <CustomTextInput
                inputRef={searchInputRef}
                id={`${instanceId.current}_search`}
                size="small"
                fullWidth
                autoComplete="off"
                spellCheck={false}
                label="Search"
                value={searchTerm}
                variant="standard"
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleListKeyDown}
                InputProps={{
                  endAdornment: searchTerm ? (
                    <InputAdornment position="end">
                      <Close
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSearchTerm("");
                        }}
                        sx={{ cursor: "pointer", color: "#AF3241" }}
                      />
                    </InputAdornment>
                  ) : undefined,
                }}
              />
            </Box>

            {/* Bulk selection row for all currently filtered options */}
            {isSearchActive && (
              <StyledMenuItemContainer
                ref={(el) => {
                  optionRefs.current["item-0"] = el;
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectAllFiltered();
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseEnter={() => setHighlightedIndex(0)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor:
                    highlightedIndex === 0 ? "rgba(175, 50, 65, 0.08)" : "transparent",
                }}
              >
                <StyledCheckbox
                  checked={allFilteredSelected}
                  indeterminate={someFilteredSelected}
                  onMouseDown={(e) => e.stopPropagation()}
                />
                <StyledMenuListItemText primary="Select All Results" />
              </StyledMenuItemContainer>
            )}

            {optionsToList.map((cat) => {
              const categoryLabel = String(cat?.[categoryLabelKey] ?? "");
              const catKey = categoryLabel;
              const allOptionsRaw = Array.isArray(cat?.[optionsKey]) ? [...cat[optionsKey]] : [];
              const allOptions = allOptionsRaw.sort((a, b) => {
                const av = String(a?.[optionValueKey] ?? "");
                const bv = String(b?.[optionValueKey] ?? "");
                return av.localeCompare(bv, undefined, { numeric: true });
              });
              const optionValues = allOptions
                .map((o) => o?.[optionValueKey])
                .filter((x) => x !== undefined && x !== null);

              const selectedCount = optionValues.filter((v) => isSelected(v)).length;
              const allSelected = optionValues.length > 0 && selectedCount === optionValues.length;
              const someSelected = selectedCount > 0 && !allSelected;
              const isExpanded = !!expanded[catKey];

              flatIndex++;
              const groupIndex = flatIndex;

              return (
                <Box
                  key={catKey}
                  ref={(el) => { itemRefs.current[catKey] = el; }}
                  sx={{ width: "100%" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <StyledMenuItemContainer
                    ref={(el) => { optionRefs.current[`item-${groupIndex}`] = el; }}
                    onClick={() => handleCategoryToggle(catKey)}
                    onMouseEnter={() => setHighlightedIndex(groupIndex)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      marginRight: 0,
                      backgroundColor:
                        highlightedIndex === groupIndex
                          ? "rgba(175, 50, 65, 0.08)"
                          : "transparent",
                    }}
                  >
                    <StyledCheckbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onClick={(e) => handleCategorySelect(optionValues, allSelected, e)}
                      onMouseDown={(e) => e.stopPropagation()}
                    />
                    <StyledMenuListItemText primary={categoryLabel} />
                    {isExpanded ? (
                      <ExpandLessIcon fontSize="small" sx={{ ml: 1 }} />
                    ) : (
                      <ExpandMoreIcon fontSize="small" sx={{ ml: 1 }} />
                    )}
                  </StyledMenuItemContainer>

                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    {allOptions.map((opt) => {
                      const optValue = opt?.[optionValueKey];
                      const optLabel = opt?.[optionLabelKey];

                      if (optValue === undefined || optValue === null) return null;

                      flatIndex++;
                      const optIndex = flatIndex;

                      return (
                        <StyledSubMenuItemContainer
                          key={String(optValue)}
                          ref={(el) => { optionRefs.current[`item-${optIndex}`] = el; }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelect(optValue);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          onMouseEnter={() => setHighlightedIndex(optIndex)}
                          sx={{
                            pl: 5,
                            display: "flex",
                            alignItems: "center",
                            marginRight: 0,
                            backgroundColor:
                              highlightedIndex === optIndex
                                ? "rgba(175, 50, 65, 0.08)"
                                : "transparent",
                          }}
                        >
                          <StyledCheckbox
                            checked={isSelected(optValue)}
                            onMouseDown={(e) => e.stopPropagation()}
                          />
                          <StyledMenuListItemText
                            primary={`${optValue} - ${optLabel ?? ""}`.trim()}
                          />
                        </StyledSubMenuItemContainer>
                      );
                    })}
                  </Collapse>
                </Box>
              );
            })}

            {optionsToList.length === 0 && (
              <Box sx={{ p: 2, textAlign: "center" }}>
                <StyledMenuListItemText primary="No options found" />
              </Box>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default CustomizedHierarchicalMultiselect;
